<template>
  <div class="tabs-container d-flex flex-row fl-start fl-a-e">
    <div
      class="tab more-tabs"
      @click="toggleDropDownMenu"
      v-if="bookings.length > maxBookingTabsToShow"
    >
      <div class="inner-tab">
        <p class="tab-name-wrapper">
          <span class="tab-name"
            >+{{ bookings.length - maxBookingTabsToShow }}
            {{ $t("tabs.more-orders") }}</span
          >
        </p>
      </div>
      <SvgIcon
        :iconContent="currentChevron"
        iconStyle="width: 16px; height: 20px;"
      />
      <transition name="dropdown">
        <div v-if="isDropdownVisible" class="dropdown-menu">
          <ul>
            <li
              v-for="(booking, index) in bookingHiddenTabs"
              :key="index"
              @click="selectBooking(booking.uuid, { fromMoreOrders: true })"
            >
              {{ getTabName(booking.uuid) }}
            </li>
          </ul>
        </div>
      </transition>
    </div>

    <div
      class="tab"
      v-for="(booking, index) in bookingVisibleTabs"
      :key="index"
      :class="getTabClass(booking.uuid)"
      @click="selectBooking(booking.uuid)"
      :draggable="true"
      @dragstart="onDragStart(index)"
      @dragover.prevent
      @drop="onDrop(index)"
    >
      <div class="inner-tab" @click="showTabNameModal(booking.uuid)">
        <SvgIcon
          v-if="booking.uuid === selectedBookingUUID"
          :iconContent="editPen"
          iconStyle="width: 20px; height: 20px;"
        />
        <p class="tab-name-wrapper">
          <span class="tab-name" data-cy="booking-tab">
            {{ getTabName(booking.uuid) }}</span
          >
        </p>
      </div>
      <span
        class="close-button"
        v-if="booking.uuid === selectedBookingUUID"
        @click="closeTab()"
        data-cy="close-booking-tab-button"
      ></span>
    </div>
    <div
      v-if="hasSalesStarted"
      class="new-tab"
      @click="createNewBooking()"
      data-cy="add-booking-button"
    >
      <p>+</p>
    </div>
  </div>
</template>

<script lang="ts">
import { makeHoldTokenExpire } from "@/api/seats-io/seats-io";
import { PSBErrors } from "@/constants/errors/product-slot-booking";
import { LogService } from "@/services/logging";
import PaymentService from "@/services/payment-service";
import BookingModule from "@/store/modules/booking";
import CashFloatModule from "@/store/modules/cash-float";
import CustomersModule from "@/store/modules/customers";
import FiskalyModule from "@/store/modules/fiskaly";
import POSConfigModule from "@/store/modules/pos-config";
import {
  handlePendingPaymentsCheck,
  bookings,
  maxBookingTabs,
  maxBookingTabsToShow,
  selectBooking,
  canCreateNewBooking,
  canCloseBookingTab,
} from "@/utils/vue-helpers/booking";
import { cloneDeep } from "lodash";
import { computed, defineComponent, ref } from "vue";
import { useI18n } from "vue-i18n";
import editPen from "@/assets/images/svg/edit-pen-rectangle.svg";
import chevronDown from "@/assets/images/svg/chevron-down.svg";
import chevronUp from "@/assets/images/svg/chevron-up.svg";
import ModalModule from "@/store/modules/modal";
import { ModalStatus } from "@/constants/modal";
import i18n from "@/lang";
import { BookingState } from "@/types/db/product-slot-booking";
import { processAbortEvent } from "@/utils/helpers/compliance";

// modules

export default defineComponent({
  name: "NavbarTabs",
  async setup() {
    const { t } = useI18n({});
    const isDropdownVisible = ref(false);
    const draggedTabIndex = ref<number | null>(null);

    const selectedBookingUUID = computed(() => {
      return BookingModule.selectedBookingUUID;
    });

    const hasSalesStarted = computed(() => {
      return CashFloatModule.cashFloat;
    });

    const currentChevron = computed(() => {
      return isDropdownVisible.value ? chevronUp : chevronDown;
    });

    const maxBookingTabs = computed(() => {
      return POSConfigModule.maxBookingTabs;
    });

    const bookingVisibleTabs = computed(() => {
      return BookingModule.bookingVisibleTabs;
    });

    const bookingHiddenTabs = computed(() => {
      return BookingModule.bookingHiddenTabs;
    });

    function createNewBooking() {
      if (!canCreateNewBooking()) {
        return;
      }
      BookingModule.createNewBooking();
    }

    async function closeTab() {
      if (!canCloseBookingTab()) {
        return;
      }
      const completedBooking = cloneDeep(BookingModule.selectedBooking);
      if (!completedBooking) {
        throw new Error(PSBErrors.NoBookingInModule);
      }
      LogService.info(
        `User clicked on Close Tab button to remove the booking, booking: ${completedBooking.uuid}`
      );

      // we check if need to prompt user for confirmation if booking is partially paid or has pending adyen payments
      const { canClose, promptedForConfirmation } =
        await handlePendingPaymentsCheck(completedBooking);
      if (!canClose) {
        console.log("user shouldn't close");
        return;
      }

      if (
        !promptedForConfirmation &&
        BookingModule.currentOrderSessionId &&
        completedBooking.state !== BookingState.Completed
      ) {
        // Booking not completed, process abort event
        try {
          await processAbortEvent(
            completedBooking,
            BookingModule.currentOrderSessionId
          );
        } catch (error) {
          LogService.info("Error processing ABORT event", {
            error,
          });
        }
      }

      console.log("removing booking");

      // remove customer of the booking
      const selectedCustomer = CustomersModule.selectedCustomers.find(
        (c: any) => c.bookingUuid === completedBooking.uuid
      );
      if (selectedCustomer) {
        CustomersModule.removeSelectedCustomer(selectedCustomer);
      }
      // remove previous booking
      BookingModule.removeBooking(completedBooking);
      // Create new booking if we have only one booking
      if (bookings.value.length === 0) {
        BookingModule.resetBookingCount();
        BookingModule.createNewBooking();
      }

      // We need to remove the booking receipt signature from Fiskaly state
      FiskalyModule.removeSignatureQrCodes(completedBooking.uuid as string);
      // remove the transaction from Fiskaly state
      FiskalyModule.removeTransaction(completedBooking.uuid as string);

      // Now if the booking in some moment of its life the user selected a seat from a chart without confirming its PSS
      // then we should make its holdToken expires immediately so that seats io release the held seats (only held seats, booked seats will not be released by this)
      if (
        completedBooking.seatsIoHoldToken &&
        completedBooking.seatsIoHoldToken.holdToken
      ) {
        const res = await makeHoldTokenExpire(
          completedBooking.seatsIoHoldToken.holdToken
        );
      }

      // TODO select the last created booking
      const nextBookingUUID = bookings.value[bookings.value.length - 1].uuid;
      selectBooking(nextBookingUUID);
      // BookingModule.setBookingStep(BookingStep.Order);
    }

    function getTabClass(uuid: string | null) {
      let className = "";

      if (
        bookings.value.length === 1 ||
        uuid === BookingModule.selectedBookingUUID
      ) {
        className = "selected-tab";
        if (BookingModule.isTabAnimated) {
          className = "selected-tab tab-with-animation";
          // Lets now set the animation to false after making sure the animation has finished
          setTimeout(stopAnimation, 1700);
        }
      } else {
        className = "unselected-tab";
      }

      return className;
    }

    function stopAnimation() {
      BookingModule.setTabAnimation(false);
    }

    function showTabNameModal(uuid: string | null) {
      if (uuid === selectedBookingUUID.value) {
        ModalModule.setStatus(ModalStatus.TabNameEdit);
      }
    }

    function getTabName(uuid: string | null) {
      const bookingCount = BookingModule.currentBookingCount;
      const defaultTabName = `${t("tabs.order")} #${bookingCount}`;
      if (!uuid) {
        BookingModule.setBookingTabName(defaultTabName);
        return defaultTabName;
      }
      const bookingTabName = BookingModule.bookings[uuid].tabName;
      if (!bookingTabName) {
        // set booking tab name to default one so it doesn't change with index change
        BookingModule.setBookingTabName(defaultTabName);
        return defaultTabName;
      }
      return bookingTabName;
    }

    function toggleDropDownMenu() {
      isDropdownVisible.value = !isDropdownVisible.value;
    }

    function onDragStart(index: number) {
      draggedTabIndex.value = index;
    }
    function onDrop(index: number) {
      if (draggedTabIndex.value !== null) {
        const uuid1 = bookings.value[
          index + bookings.value.length - maxBookingTabsToShow
        ].uuid as string;
        const uuid2 = bookings.value[
          draggedTabIndex.value + bookings.value.length - maxBookingTabsToShow
        ].uuid as string;
        BookingModule.swapBookings({ uuid1, uuid2 });
        draggedTabIndex.value = null; // Reset dragged index
      }
    }

    return {
      t,
      bookings,
      selectedBookingUUID,
      createNewBooking,
      selectBooking,
      closeTab,
      hasSalesStarted,
      getTabClass,
      editPen,
      currentChevron,
      showTabNameModal,
      getTabName,
      isDropdownVisible,
      toggleDropDownMenu,
      maxBookingTabs,
      maxBookingTabsToShow,
      bookingVisibleTabs,
      bookingHiddenTabs,
      draggedTabIndex,
      onDragStart,
      onDrop,
    };
  },
});
</script>

<style lang="scss">
@use "@/styles/variables.scss" as *;

$height: 35px;
.tabs-container {
  .tab {
    margin-right: 5px;
    height: $height;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    border-radius: 10px 10px 0px 0px;
    cursor: pointer;

    p {
      line-height: 16px;
      height: 16px;
      letter-spacing: 0px;
      font-weight: 700;
      font-style: normal;
      font-size: 14px;
      text-align: center;
      position: relative;
      width: 100%;
    }

    .circle {
      display: inline-block;
      height: 25px;
      width: 25px;
      border-radius: 50%;
      position: absolute;
      top: -5px;
      left: 4px;
      font-weight: 400;
      font-style: normal;
      font-size: 13px;
      text-align: center;
      line-height: 25px;
      letter-spacing: 0px;
      color: white;
      background-color: $color-green-eagle;
    }
    .close-button {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 18px;
      height: 18px;
      font-weight: bold;
      font-style: normal;
      border: 1.5px solid black;
      border-radius: 50%;
      font-size: 12px;
      color: black;
      text-align: center;
      line-height: 1.5;
      cursor: pointer;
      margin-right: 5px;
    }

    .close-button::before {
      content: "x";
    }
  }

  .new-tab {
    background-color: transparent;
    margin-right: 5px;
    height: $height;
    width: 38px;
    line-height: $height;
    letter-spacing: 0px;
    color: rgb(255, 255, 255);
    font-weight: 700;
    font-style: normal;
    font-size: 18px;
    text-align: center;
    border-radius: 4px 4px 0px 0px;
    border: 1px solid white;
    border-bottom: none;
    cursor: pointer;
  }
  .selected-tab {
    width: 170px;
    background-color: #fafafa;
    color: $color-green-eagle;
  }
  .tab-with-animation {
    width: 170px;
    animation: colorSizeAnimation 1.5s ease-in-out forwards;
  }
  .unselected-tab {
    width: 120px;
    background-color: #0e2d34;
    color: #fafafa;
  }

  .inner-tab {
    display: flex;
    align-items: center;
    padding-left: 5px;
  }

  .selected-tab .tab-name-wrapper {
    max-width: 120px /* approx 70% of tab width */;
  }
  .unselected-tab .tab-name-wrapper {
    max-width: 110px /* adjusted to margin added */;
  }

  .tab-name-wrapper {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .selected-tab .tab-name {
    padding-left: 30px;
    margin-left: -1.5rem;
    text-decoration: underline;
  }

  .unselected-tab .tab-name {
    padding-left: 20px;
  }

  .more-tabs {
    position: relative;
    width: 200px;
    background-color: #0e2d34;
    color: #fafafa;
    padding: 12px;
  }

  .dropdown-menu {
    position: absolute;
    top: 100%;
    left: 0;
    background-color: #fff;
    color: $color-green-eagle;
    border: 1px solid #ccc;
    border-radius: 5px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    width: 200px;
    z-index: 10;
    font-weight: 600;
  }
  .dropdown-menu ul {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  .dropdown-menu li {
    padding: 15px;
    cursor: pointer;
  }

  .dropdown-menu li:not(:last-child) {
    border-bottom: 1px solid $color-green-eagle; /* Add a separator line */
  }

  .dropdown-menu li:hover {
    background-color: #f0f0f0;
  }

  /* Animation styles */
  .dropdown-enter-active,
  .dropdown-leave-active {
    transition: opacity 0.3s ease, transform 0.3s ease;
  }

  .dropdown-enter-from,
  .dropdown-leave-to {
    opacity: 0;
    transform: translateY(-10px);
  }

  @keyframes colorSizeAnimation {
    0% {
      background-color: #0e2d34;
    }

    50% {
      background-color: orange;
    }

    100% {
      background-color: #fafafa;
    }
  }
}
</style>
